<template>
    <div class="weeduc-admin-page course-group-form-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="editingId ? t.formTitleEdit : t.formTitleCreate"
                    :subtitle="editingId ? t.formSubtitleEdit : t.formSubtitleCreate"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    v-if="!editingId"
                    variant="tonal"
                    color="primary"
                    rounded="lg"
                    class="text-none"
                    prepend-icon="mdi-auto-fix"
                    @click="fillDemoData"
                >
                    {{ t.fillDemoData }}
                </v-btn>
                <v-btn variant="text" class="text-none" @click="goBack">
                    {{ t.cancel }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    :loading="saving"
                    @click="save"
                >
                    {{ t.save }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="loading" class="course-group-form-placeholder">
                <Loader :isActive="false" />
            </div>
            <div v-else class="weeduc-admin-page__panel">
                <div class="corporate-groups-form">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <GroupBasicProductFields v-model="form" />
                            <div class="corporate-groups-form__access-banner">
                                <p class="corporate-dialog__hint">
                                    Defina quais alunos terão acesso em Liberação.
                                </p>
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="lg"
                                    class="text-none"
                                    @click="goToLiberacao"
                                >
                                    Abrir Liberação
                                </v-btn>
                            </div>
                        </div>
                    </section>

                    <section class="corporate-dialog__section">
                        <div class="course-groups-form__items-head">
                            <p class="corporate-dialog__section-label">{{ t.sectionCourses }}</p>
                            <v-btn
                                size="small"
                                variant="tonal"
                                color="primary"
                                class="text-none"
                                :disabled="!availableCourseOptions.length"
                                @click="addCourse"
                            >
                                {{ t.addCourse }}
                            </v-btn>
                        </div>
                        <div class="corporate-dialog__section-body">
                            <p v-if="!form.courseIds.length" class="course-groups-form__empty">
                                {{ t.coursesEmpty }}
                            </p>
                            <div
                                v-for="(courseId, index) in form.courseIds"
                                :key="`course-${index}-${courseId}`"
                                class="course-groups-form__item"
                            >
                                <span class="course-groups-form__item-order">{{ index + 1 }}</span>
                                <v-select
                                    :model-value="courseId"
                                    :items="courseSelectOptions(index)"
                                    item-title="text"
                                    item-value="value"
                                    :label="t.course"
                                    variant="solo"
                                    density="compact"
                                    flat
                                    class="weeduc-admin-v-field course-groups-form__item-field"
                                    hide-details
                                    @update:model-value="(value) => setCourseAt(index, value)"
                                />
                                <div class="course-groups-form__item-actions">
                                    <v-btn
                                        icon="mdi-chevron-up"
                                        variant="text"
                                        size="small"
                                        :disabled="index === 0"
                                        @click="moveCourse(index, -1)"
                                    />
                                    <v-btn
                                        icon="mdi-chevron-down"
                                        variant="text"
                                        size="small"
                                        :disabled="index === form.courseIds.length - 1"
                                        @click="moveCourse(index, 1)"
                                    />
                                    <v-btn
                                        icon="mdi-delete-outline"
                                        variant="text"
                                        size="small"
                                        color="error"
                                        @click="form.courseIds.splice(index, 1)"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section v-if="form.priceEnabled" class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.sectionPrice }}</p>
                        <div class="corporate-dialog__section-body">
                            <v-select
                                v-model="form.priceMode"
                                :items="priceModeOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.priceMode"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                            />
                            <p class="corporate-dialog__hint">
                                Em “Valor específico”, use Preço/Desconto dos dados do grupo.
                                Em “Soma dos cursos”, o valor é calculado automaticamente.
                            </p>
                            <div class="course-groups-price-preview">
                                <span>{{ t.pricePreview }}</span>
                                <strong>{{ formatMoney(formPreviewPrice) }}</strong>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import GroupBasicProductFields from '@/components/Admin/GroupBasicProductFields.vue';
import {
    emptyGroupProductFields,
    mapGroupProductFromRow,
    toGroupProductPayload,
    buildDemoGroupProductFields,
    createDemoCoverFile,
} from '@/utils/groupProductFields';
import { courseGroupsI18n } from '@/i18n/mixins';
import courseGroupsApi from '@/utils/corporate/courseGroups';
import coursesApi, { unwrapCoursesList } from '@/utils/courses';
import apiAWS from '@/utils/storage/aws';

const emptyForm = () => ({
    ...emptyGroupProductFields(),
    courseIds: [],
    priceMode: 'sum',
    customPrice: 0,
});

export default {
    name: 'SchoolCourseGroupForm',
    mixins: [courseGroupsI18n],
    components: { TitlePage, Loader, GroupBasicProductFields },
    props: {
        groupId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            courses: [],
            form: emptyForm(),
        };
    },
    computed: {
        ...mapState(['token']),
        editingId() {
            return this.groupId || this.$route.query.id || null;
        },
        priceModeOptions() {
            return [
                { text: this.t.priceModeSum, value: 'sum' },
                { text: this.t.priceModeCustom, value: 'custom' },
            ];
        },
        courseOptions() {
            return (this.courses || []).map((course) => ({
                text: course.name || course.comercialName || course.title || `Curso ${course.id}`,
                value: String(course.id),
                price: this.courseFinalPrice(course),
            }));
        },
        availableCourseOptions() {
            const selected = new Set(this.form.courseIds.map(String));
            return this.courseOptions.filter((opt) => !selected.has(String(opt.value)));
        },
        formPreviewPrice() {
            return this.resolveGroupPrice(this.form);
        },
    },
    watch: {
        editingId: {
            immediate: true,
            handler() {
                this.bootstrap();
            },
        },
    },
    methods: {
        async bootstrap() {
            this.loading = true;
            try {
                await this.loadCourses();
                if (this.editingId) {
                    await this.loadGroup();
                } else {
                    this.form = emptyForm();
                }
            } finally {
                this.loading = false;
            }
        },
        courseFinalPrice(course) {
            const price = Number(course?.price) || 0;
            const discount = Number(course?.discount) || 0;
            return Math.max(0, price - discount);
        },
        resolveGroupPrice(group) {
            if (!group) return 0;
            if (group.priceMode === 'custom') {
                const fromFields = Math.max(
                    0,
                    (Number(group.price) || 0) - (Number(group.discount) || 0),
                );
                return fromFields || Number(group.customPrice) || 0;
            }
            return (group.courseIds || []).reduce((sum, id) => {
                const course = this.courses.find((c) => String(c.id) === String(id));
                return sum + this.courseFinalPrice(course);
            }, 0);
        },
        formatMoney(value) {
            return Number(value || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
        },
        courseSelectOptions(index) {
            const current = this.form.courseIds[index];
            const selected = new Set(
                this.form.courseIds.filter((_, i) => i !== index).map(String),
            );
            return this.courseOptions.filter(
                (opt) => String(opt.value) === String(current) || !selected.has(String(opt.value)),
            );
        },
        async loadCourses() {
            try {
                const response = await coursesApi.getDataCourse(this.token);
                this.courses = unwrapCoursesList(response);
            } catch (e) {
                this.courses = [];
            }
        },
        async loadGroup() {
            try {
                const row = await courseGroupsApi.getById(this.editingId, this.token);
                const product = mapGroupProductFromRow(row);
                if (row.priceMode === 'custom' && !(Number(product.price) > 0) && Number(row.customPrice) > 0) {
                    product.price = Number(row.customPrice) || 0;
                }
                product.priceEnabled =
                    (Number(product.price) || 0) > 0
                    || (Number(product.discount) || 0) > 0;
                const courseIds = (row.courseIds && row.courseIds.length)
                    ? row.courseIds.map(String)
                    : (row.items || []).map((item) => String(item.courseId)).filter(Boolean);
                this.form = {
                    ...product,
                    courseIds,
                    priceMode: row.priceMode === 'custom' ? 'custom' : 'sum',
                    customPrice: Number(row.customPrice) || 0,
                };
            } catch (e) {
                alert(this.t.error);
                this.goBack();
            }
        },
        async resolveCoverImageUrl() {
            if (this.form.coverFile instanceof File) {
                try {
                    const signature = await apiAWS.getSignatureImage(
                        this.token,
                        this.form.coverFile.name,
                    );
                    await apiAWS.putFileAWS(signature.url, this.form.coverFile);
                    return signature.token;
                } catch (e) {
                    return this.form.imageUrl || URL.createObjectURL(this.form.coverFile);
                }
            }
            return this.form.imageUrl || null;
        },
        addCourse() {
            const next = this.availableCourseOptions[0];
            if (!next) return;
            this.form.courseIds.push(String(next.value));
        },
        setCourseAt(index, value) {
            if (!value) return;
            this.form.courseIds.splice(index, 1, String(value));
        },
        moveCourse(index, direction) {
            const target = index + direction;
            if (target < 0 || target >= this.form.courseIds.length) return;
            const ids = [...this.form.courseIds];
            const [item] = ids.splice(index, 1);
            ids.splice(target, 0, item);
            this.form.courseIds = ids;
        },
        goBack() {
            if (this.editingId) {
                this.$router.push(`/dashboard/grupo/${this.editingId}`);
                return;
            }
            this.$router.push('/dashboard/cursos');
        },
        goToDetail(groupId) {
            this.$router.push(`/dashboard/grupo/${groupId}`);
        },
        goToLiberacao() {
            this.$router.push('/dashboard/liberacoes');
        },
        async fillDemoData() {
            const demo = buildDemoGroupProductFields();
            const courseIds = (this.courses || [])
                .slice(0, 2)
                .map((c) => String(c.id))
                .filter(Boolean);
            this.form = {
                ...this.form,
                ...demo,
                courseIds,
                priceMode: courseIds.length ? 'sum' : 'custom',
                customPrice: courseIds.length ? 0 : 249.9,
            };
            try {
                const coverFile = await createDemoCoverFile(
                    `${demo.name} capa`,
                    'pacote-capa-demo.png',
                );
                this.form = {
                    ...this.form,
                    coverFile,
                    imageUrl: '',
                };
            } catch (e) {
                console.warn('[school-course-group-form] falha ao gerar capa demo', e);
            }
        },
        async save() {
            if (!this.form.name?.trim()) {
                alert(this.t.nameRequired);
                return;
            }
            if (!this.form.courseIds.length) {
                alert(this.t.coursesRequired);
                return;
            }
            if (this.form.priceEnabled) {
                const price = Number(this.form.price) || 0;
                const discount = Number(this.form.discount) || 0;
                if (discount < 0 || price < 0) {
                    alert(this.t.discountInvalid);
                    return;
                }
                if (discount > price) {
                    alert(this.t.discountInvalid);
                    return;
                }
            }
            const finalPrice = Math.max(
                0,
                (Number(this.form.price) || 0) - (Number(this.form.discount) || 0),
            );
            this.saving = true;
            try {
                const imageUrl = await this.resolveCoverImageUrl();
                const product = toGroupProductPayload({ ...this.form, imageUrl });
                if (this.form.priceEnabled && this.form.priceMode === 'sum') {
                    product.price = this.resolveGroupPrice({ ...this.form, priceMode: 'sum' });
                }
                const payload = {
                    ...product,
                    courseIds: this.form.courseIds.map(String),
                    items: this.form.courseIds.map((courseId, index) => ({
                        courseId: String(courseId),
                        order: index + 1,
                        unlockRule: 'always',
                    })),
                    priceMode: this.form.priceMode === 'custom' ? 'custom' : 'sum',
                    customPrice: this.form.priceMode === 'custom' ? finalPrice : 0,
                };
                let savedId = this.editingId;
                if (this.editingId) {
                    await courseGroupsApi.update(this.editingId, payload, this.token);
                } else {
                    const created = await courseGroupsApi.create(payload, this.token);
                    savedId = created?.id;
                }
                if (savedId) {
                    this.goToDetail(savedId);
                } else {
                    this.$router.push('/dashboard/cursos');
                }
            } catch (e) {
                alert(this.t.error);
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.course-group-form-placeholder {
    padding: 2.5rem 1.5rem;
    text-align: center;
}

.corporate-groups-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.corporate-groups-form__access-banner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: #eef1fb;
}

.corporate-groups-form__access-banner .corporate-dialog__hint {
    margin: 0;
    flex: 1;
    min-width: 200px;
}

.course-groups-form__items-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
}

.course-groups-form__items-head .corporate-dialog__section-label {
    margin: 0;
}

.course-groups-form__empty {
    margin: 0;
    padding: 1.1rem 0.75rem;
    text-align: center;
    font-size: 0.875rem;
    color: #7a8190;
    background: #fff;
    border-radius: 10px;
    border: 1px dashed #e2e5ec;
}

.course-groups-form__item {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr) auto;
    gap: 0.5rem;
    align-items: center;
    padding: 0.45rem 0.55rem;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #eef0f4;
}

.course-groups-form__item-order {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #eef0fc;
    color: #250891;
    font-size: 0.8rem;
    font-weight: 700;
}

.course-groups-form__item-field :deep(.v-field) {
    background: transparent !important;
    box-shadow: none !important;
}

.course-groups-form__item-field :deep(.v-field__overlay) {
    opacity: 0;
}

.course-groups-form__item-actions {
    display: flex;
    align-items: center;
    gap: 0.1rem;
    flex-shrink: 0;
}

.course-groups-price-preview {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #eef0f4;
    font-size: 0.9rem;
    color: #5c6370;
}

.course-groups-price-preview strong {
    color: #1a1d26;
    font-size: 1.05rem;
}

@media (max-width: 720px) {
    .course-groups-form__item {
        grid-template-columns: 1fr;
    }

    .course-groups-form__item-actions {
        justify-content: flex-end;
    }
}
</style>
